/**
 * CR-7352: "My Collections" — the personal queue of rejected-goods
 * collections assigned to the signed-in user.
 *
 * The NCR register answers "what is wrong"; it cannot answer "what is mine".
 * This page does: every open collection assigned to me, oldest assignment
 * last, with my own step reachable without opening the NCR -- mark the goods
 * collected once the supplier has confirmed the date, or complete early before it has.
 * A collected row waits on the supplier's receipt; a row leaves the list when
 * the collection completes.
 *
 * Customer-side only; suppliers are never assignees.
 *
 * @link ncr-edit/CollectionAssignment for where the assignment is made.
 */
import { useCallback, useEffect, useState } from 'react';

import { CheckCircle2, Loader2, Package, Search } from 'lucide-react';
import { toast } from 'sonner';

import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader } from '../components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Pagination } from '../components/ui/pagination';
import { Textarea } from '../components/ui/textarea';
import { useMyCollectionsCount } from '../components/quality/useMyCollectionsCount';
import { QualityTabStrip } from '../components/quality/QualityTabStrip';
import { platformSettingsAPI, qualityAPI } from '../lib/api';
import type { MyCollectionQueueRow, QualityTabsVisibility } from '../lib/api';
import { useFormatters } from '../contexts/PreferencesContext';
import { useView } from '../contexts/ViewContext';
import { getErrorMessage } from '../hooks/useFetchData';
import { getApiErrorMessage } from '../lib/api/types/common';
import { todayLocalISO } from './ncr-edit/localDate';
import { usePagination } from '../hooks';
import { useIsCustomer } from '../hooks/usePortalMode';


/** What the row is waiting on, in the words the Collection tab uses. */
const STATUS_LABELS: Record<string, string> = {
  requested: 'Not started — initiate it on the NCR',
  pending: 'Collection requested — awaiting supplier date',
  date_proposed: 'Supplier proposed a date — your answer needed',
  confirmed: 'Date confirmed — awaiting collection',
  collected: 'Collected — awaiting supplier receipt',
};

const STATUS_STYLES: Record<string, string> = {
  requested: 'bg-blue-100 text-blue-800',
  pending: 'bg-amber-100 text-amber-800',
  date_proposed: 'bg-sky-100 text-sky-800',
  confirmed: 'bg-blue-100 text-blue-800',
  collected: 'bg-emerald-50 text-emerald-800',
};

/**
 * What the assignee's button does on a row:
 * once the supplier has confirmed the date, their step is "Goods collected"; before
 * that it is CR-7352's early completion; once collected, the supplier owes
 * the receipt and there is nothing to press.
 */
function queueAction(status: string): 'collected' | 'early' | null {
  if (status === 'confirmed') return 'collected';
  if (status === 'requested' || status === 'pending' || status === 'date_proposed') return 'early';
  return null;
}

export default function MyCollections() {
  const isCustomer = useIsCustomer();
  const { formatDate } = useFormatters();
  const { getViewQueryParams, viewKey, isLoading: viewLoading } = useView();

  const [rows, setRows] = useState<MyCollectionQueueRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');

  // Completion dialog state -- the actual collection date is required, the
  // notes optional, as on the NCR's own Collection tab.
  const [completing, setCompleting] = useState<MyCollectionQueueRow | null>(null);
  const [receiptNotes, setReceiptNotes] = useState('');
  const [collectedOn, setCollectedOn] = useState(todayLocalISO());
  const [saving, setSaving] = useState(false);

  const [tabVisibility, setTabVisibility] = useState<QualityTabsVisibility>({
    ncrs: true,
    concessions: true,
    approval_register: true,
  });
  const [concessionsEnabled, setConcessionsEnabled] = useState(false);

  const pagination = usePagination({ pageSize: 15, onPageChange: () => {} });
  const { count, covering, refresh: refreshCount } = useMyCollectionsCount(isCustomer);

  // Same tab-visibility fetch as Quality.tsx / ApprovalRegister.tsx so the
  // strip honours the platform-settings toggles.
  useEffect(() => {
    platformSettingsAPI
      .getDisplaySettings()
      .then((res) => {
        if (res.data?.quality_tabs) {
          setTabVisibility((prev) => ({ ...prev, ...res.data.quality_tabs }));
        }
        if (res.data) setConcessionsEnabled(!!res.data.concessions_enabled);
      })
      .catch(() => undefined);
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchTerm);
      pagination.reset();
    }, 500);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm]);

  const fetchRows = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await qualityAPI.getMyCollections({
        page: pagination.currentPage,
        page_size: pagination.pageSize,
        search: debouncedSearch || undefined,
        ...getViewQueryParams(),
      });
      setRows(res.data.results);
      pagination.setFromResponse(res.data.count, (res.data.results || []).length);
    } catch (err: unknown) {
      setError(getErrorMessage(err, 'Failed to load your collections'));
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pagination.currentPage, pagination.pageSize, debouncedSearch, getViewQueryParams]);

  useEffect(() => {
    if (viewLoading || !isCustomer) return;
    fetchRows();
    // viewKey changes when the top-bar picker moves company / site.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchRows, viewLoading, viewKey, isCustomer]);

  const handleComplete = async () => {
    if (!completing) return;
    try {
      setSaving(true);
      const res = await qualityAPI.completeCollection(completing.ncr, {
        actual_collection_date: collectedOn,
        customer_notes: receiptNotes || undefined,
      });
      toast.success(res.data?.status === 'collected'
        ? `Goods marked collected for ${completing.ncr_number}; the supplier confirms receipt next`
        : `Collection completed for ${completing.ncr_number}`);
      setCompleting(null);
      setReceiptNotes('');
      await fetchRows();
      refreshCount();
    } catch (err: unknown) {
      console.error('Error completing collection:', err);
      toast.error(getApiErrorMessage(err, 'Failed to complete the collection'));
    } finally {
      setSaving(false);
    }
  };

  const tabStrip = (
    <CardHeader className="border-b bg-white px-6 py-4">
      <QualityTabStrip
        active="my_collections"
        tabVisibility={tabVisibility}
        concessionsEnabled={concessionsEnabled}
        isCustomer={isCustomer}
        myCollectionsCount={count}
      />
    </CardHeader>
  );

  if (!isCustomer) {
    return (
      <Card className="rounded-lg shadow-sm">
        <CardContent className="py-12 text-center text-muted-foreground">
          Collections are assigned and completed by the customer, so this queue
          is not available on the supplier portal.
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-0">
      <Card className="rounded-lg shadow-sm">
        {tabStrip}
        <CardContent className="p-6 space-y-4">
          <div className="flex items-center justify-between gap-4">
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search NCR, part or supplier..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-9"
              />
            </div>
            <div className="text-sm text-muted-foreground">
              {count} open {count === 1 ? 'collection' : 'collections'} assigned to you
              {/* CR-5889: rows can also be someone's you cover for. */}
              {covering > 0 && ' or to someone you cover for'}
            </div>
          </div>

          {loading ? (
            <div className="flex items-center justify-center min-h-[300px]">
              <Loader2 className="h-8 w-8 animate-spin" />
            </div>
          ) : error ? (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <p className="text-red-800">Error: {error}</p>
              <button onClick={fetchRows} className="mt-2 text-sm text-red-600 hover:text-red-800 underline">
                Try again
              </button>
            </div>
          ) : rows.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              <Package className="h-8 w-8 mx-auto mb-3 opacity-40" />
              {debouncedSearch
                ? 'No collections assigned to you match that search'
                : 'Nothing assigned to you. Collections appear here when someone assigns one to you.'}
            </div>
          ) : (
            <>
              <Pagination
                currentPage={pagination.currentPage}
                totalPages={pagination.totalPages}
                totalCount={pagination.totalCount}
                itemsPerPage={pagination.pageSize}
                servedPerPage={pagination.servedPageSize}
                onPageChange={pagination.goToPage}
                onPageSizeChange={pagination.setPageSize}
                itemLabel="collections"
              />

              <div className="overflow-x-auto border rounded-lg">
                <table className="w-full">
                  <thead>
                    <tr className="bg-[#f3f6f9] border-b">
                      <th className="text-left py-3 px-4 font-semibold text-sm text-[#3f4254]">NCR</th>
                      <th className="text-left py-3 px-4 font-semibold text-sm text-[#3f4254]">Supplier</th>
                      <th className="text-left py-3 px-4 font-semibold text-sm text-[#3f4254]">Part</th>
                      <th className="text-left py-3 px-4 font-semibold text-sm text-[#3f4254]">Reject qty</th>
                      <th className="text-left py-3 px-4 font-semibold text-sm text-[#3f4254]">Status</th>
                      <th className="text-left py-3 px-4 font-semibold text-sm text-[#3f4254]">Collection date</th>
                      <th className="text-left py-3 px-4 font-semibold text-sm text-[#3f4254]">Assigned</th>
                      <th className="text-right py-3 px-4 font-semibold text-sm text-[#3f4254]">
                        <span className="sr-only">Actions</span>
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((row, index) => (
                      <tr
                        key={row.id}
                        className={`border-b cursor-pointer transition-colors hover:bg-[#f3f6f9] ${
                          index % 2 === 0 ? 'bg-white' : 'bg-[#f9fafb]'
                        }`}
                        style={{ height: '58px' }}
                        // Same as the NCR register: the NCR opens in a new
                        // tab (CR-6444) so the queue stays where it was.
                        onClick={() => window.open(`/quality/ncr/${row.ncr}/edit`, '_blank')}
                      >
                        <td className="py-3 px-4 text-sm font-medium text-[#3f4254]">{row.ncr_number}</td>
                        <td className="py-3 px-4 text-sm text-[#3f4254]">{row.supplier_name || '-'}</td>
                        <td className="py-3 px-4 text-sm font-mono text-[#3f4254]">{row.part_number || '-'}</td>
                        <td className="py-3 px-4 text-sm text-[#3f4254]">{row.reject_qty ?? '-'}</td>
                        <td className="py-3 px-4 text-sm">
                          <span className={`inline-flex px-2 py-1 rounded-full text-xs font-medium ${
                            STATUS_STYLES[row.status] || 'bg-gray-100 text-gray-700'
                          }`}>
                            {STATUS_LABELS[row.status] || row.status}
                          </span>
                        </td>
                        <td className="py-3 px-4 text-sm text-[#3f4254]">
                          {row.confirmed_collection_date || row.proposed_collection_date
                            ? formatDate((row.confirmed_collection_date || row.proposed_collection_date) as string)
                            : '-'}
                        </td>
                        <td className="py-3 px-4 text-sm text-[#3f4254]">
                          {row.assigned_at ? formatDate(row.assigned_at) : '-'}
                          {row.assigned_by_name && (
                            <span className="block text-xs text-muted-foreground">
                              by {row.assigned_by_name}
                            </span>
                          )}
                          {row.covering_for && (
                            <span className="block text-xs font-medium text-amber-700">
                              Covering for {row.covering_for}
                            </span>
                          )}
                        </td>
                        <td className="py-3 px-4 text-right" onClick={(e) => e.stopPropagation()}>
                          {queueAction(row.status) ? (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => { setCompleting(row); setReceiptNotes(''); setCollectedOn(todayLocalISO()); }}
                            >
                              <CheckCircle2 className="h-4 w-4 mr-2" />
                              {queueAction(row.status) === 'collected' ? 'Mark collected' : 'Complete early'}
                            </Button>
                          ) : (
                            <span className="text-xs text-muted-foreground">Supplier to confirm receipt</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <Pagination
                currentPage={pagination.currentPage}
                totalPages={pagination.totalPages}
                totalCount={pagination.totalCount}
                itemsPerPage={pagination.pageSize}
                servedPerPage={pagination.servedPageSize}
                onPageChange={pagination.goToPage}
                onPageSizeChange={pagination.setPageSize}
                itemLabel="collections"
              />
            </>
          )}
        </CardContent>
      </Card>

      <Dialog open={!!completing} onOpenChange={(open) => { if (!open) setCompleting(null); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {completing && queueAction(completing.status) === 'collected' ? 'Mark goods collected' : 'Complete collection early'}
            </DialogTitle>
            <DialogDescription>
              {completing && queueAction(completing.status) === 'collected'
                ? `Confirm the rejected parts on ${completing.ncr_number} were collected. The supplier then confirms it received them.`
                : completing
                  ? `The supplier hasn't confirmed the collection on ${completing.ncr_number} yet. Completing now closes the collection; the log records it was before supplier confirmation.`
                  : ''}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="collected-on">Actual collection date <span className="text-red-500">*</span></Label>
              <Input
                id="collected-on"
                type="date"
                max={todayLocalISO()}
                value={collectedOn}
                onChange={(e) => setCollectedOn(e.target.value)}
                className="w-48"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="receipt-notes">Notes (optional)</Label>
              <Textarea
                id="receipt-notes"
                value={receiptNotes}
                onChange={(e) => setReceiptNotes(e.target.value)}
                placeholder="Who collected, condition, reference..."
                rows={3}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCompleting(null)} disabled={saving}>
              Cancel
            </Button>
            <Button onClick={handleComplete} disabled={saving || !collectedOn}>
              {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <CheckCircle2 className="h-4 w-4 mr-2" />}
              {completing && queueAction(completing.status) === 'collected' ? 'Mark collected' : 'Complete early'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
